import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import React, { useEffect, useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import briefcase from "../../../assets/briefcase.svg";
import useEntityPlusForm from "./useEntityPlusForm";
import { currencyList } from "~/utils/currency";
import PropTypes from "prop-types";
import {
  formatDateToISOStartOfDay,
  removeTimezoneFromDateString,
} from "~/utils/dateUtil";

const EntityPlusForm = ({
  setBtnToggle,
  setBtnToggleAdd,
  entityPlusData,
  setEntityPlusData,
  isEditing,
  editMode,
  handleFormStateChange,
}) => {
  const { handleInputChange } = useEntityPlusForm();
  const initialDefaultValue = {
    vat_gst: "",
    us_fein: "",
    trade_tax_id: "",
    fatca_id: "",
    eori: "",
    local_tax_id: "",
    duns: "",
    lei_code: "",
    local_employer_number: "",
    nace: "",
    sic_naics: "",
    currency: "",
    incorporation_date: "",
    acquisition_date: "",
    sale_closure_date: "",
    sale_closure_remarks: "",
  };
  const {
    control,
    reset,
    formState: { isDirty, isValid },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      vat_gst: initialDefaultValue.vat_gst || "",
      us_fein: initialDefaultValue.us_fein || "",
      trade_tax_id: initialDefaultValue.trade_tax_id || "",
      fatca_id: initialDefaultValue.fatca_id || "",
      eori: initialDefaultValue.eori || "",
      local_tax_id: initialDefaultValue.local_tax_id || "",
      duns: initialDefaultValue.duns || "",
      lei_code: initialDefaultValue.lei_code || "",
      local_employer_number: initialDefaultValue.local_employer_number || "",
      nace: initialDefaultValue.nace || "",
      sic_naics: initialDefaultValue.sic_naics || "",
      currency: initialDefaultValue.currency || "",
      incorporation_date: initialDefaultValue.incorporation_date || "",
      acquisition_date: initialDefaultValue.acquisition_date || "",
      sale_closure_date: initialDefaultValue.sale_closure_date || "",
      sale_closure_remarks: initialDefaultValue.sale_closure_remarks || "",
    },
  });

  useEffect(() => {
    if (entityPlusData) {
      reset({
        vat_gst: entityPlusData.vat_gst || "",
        us_fein: entityPlusData.us_fein || "",
        trade_tax_id: entityPlusData.trade_tax_id || "",
        fatca_id: entityPlusData.fatca_id || "",
        eori: entityPlusData.eori || "",
        local_tax_id: entityPlusData.local_tax_id || "",
        duns: entityPlusData.duns || "",
        lei_code: entityPlusData.lei_code || "",
        local_employer_number: entityPlusData.local_employer_number || "",
        nace: entityPlusData.nace || "",
        sic_naics: entityPlusData.sic_naics || "",
        currency: entityPlusData.currency || "",
        incorporation_date: entityPlusData.incorporation_date || "",
        acquisition_date: entityPlusData.acquisition_date || "",
        sale_closure_date: entityPlusData.sale_closure_date || "",
        sale_closure_remarks: entityPlusData.sale_closure_remarks || "",
      });
    }
  }, [entityPlusData, reset]);

  const hasHandledInitialEdit = useRef(false);

  useEffect(() => {
    if (isEditing && editMode) {
      if (!hasHandledInitialEdit.current) {
        handleFormStateChange(false);
        hasHandledInitialEdit.current = true;
      } else {
        handleFormStateChange(isDirty || isValid);
      }
    } else {
      hasHandledInitialEdit.current = false;
    }
  }, [isDirty, isValid, isEditing, editMode]);

  const currencyItem = (option) => {
    return (
      <span>
        {option.symbol} - {option.label}
      </span>
    );
  };

  const valueTemplate = (option) => {
    if (option) {
      return (
        <span>
          {option.symbol} - {option.label}
        </span>
      );
    }

    return <span>{"Select Currency"}</span>;
  };
  return (
    <>
      <form>
        <div className="formgrid grid entity-plus-form">
          {!editMode && (
            <div className="field col-12 p-0 mb-4">
              <p className="configure-services">Configure Services</p>
            </div>
          )}
          <div className="flex gap-4 mb-4 entity-plus-header">
            <img src={briefcase} alt="briefcase" />
            <div className="flex flex-column gap-1">
              <p className="section-header">Entities Plus</p>
              <p className="section-description">
                Smarter management starts with complete, verified data —
                exclusively for Entity + users.
              </p>
            </div>
          </div>

          {/* Tax identification numbers */}
          <div className="flex flex-column gap-4 mb-4">
            <div className="flex flex-column gap-1">
              <p className="section-header">Tax Identification Numbers</p>
              <p className="section-description">
                These fields capture unique entity identifiers used for tax and
                legal purposes:
              </p>
            </div>

            <div className=" formgrid grid entity-plus-section">
              <div className="field col-6 general-info-margin">
                <label className="input-label">VAT/GST</label>
                <Controller
                  name="vat_gst"
                  control={control}
                  render={({ field }) => {
                    return (
                      <InputText
                        {...field}
                        placeholder="Enter VAT or GST identification number"
                        className={`input-field appearance-none outline-none w-full `}
                        value={entityPlusData?.vat_gst || ""}
                        disabled={!isEditing}
                        onChange={(e) => {
                          field.onChange(e.target.value);
                          handleInputChange(
                            field.name,
                            e.target.value,
                            entityPlusData,
                            setEntityPlusData,
                          );
                        }}
                      />
                    );
                  }}
                />
              </div>
              <div className="field col-6 general-info-margin">
                <label className="input-label">
                  US FEIN (Federal Employer Identification Number)
                </label>
                <Controller
                  name="us_fein"
                  control={control}
                  render={({ field }) => {
                    return (
                      <InputText
                        {...field}
                        placeholder="Enter US FEIN"
                        className={`input-field appearance-none outline-none w-full `}
                        value={entityPlusData?.us_fein || ""}
                        disabled={!isEditing}
                        onChange={(e) => {
                          field.onChange(e.target.value);
                          handleInputChange(
                            field.name,
                            e.target.value,
                            entityPlusData,
                            setEntityPlusData,
                          );
                        }}
                      />
                    );
                  }}
                />
              </div>
              <div className="field col-6 general-info-margin">
                <label className="input-label">Trade Tax ID</label>
                <Controller
                  name="trade_tax_id"
                  control={control}
                  render={({ field }) => {
                    return (
                      <InputText
                        {...field}
                        placeholder="Enter Trade Tax ID"
                        className={`input-field appearance-none outline-none w-full `}
                        value={entityPlusData?.trade_tax_id || ""}
                        disabled={!isEditing}
                        onChange={(e) => {
                          field.onChange(e.target.value);
                          handleInputChange(
                            field.name,
                            e.target.value,
                            entityPlusData,
                            setEntityPlusData,
                          );
                        }}
                      />
                    );
                  }}
                />
              </div>
              <div className="field col-6 general-info-margin">
                <label className="input-label">
                  FATCA ID (Foreign Account Tax Compliance Act)
                </label>
                <Controller
                  name="fatca_id"
                  control={control}
                  render={({ field }) => {
                    return (
                      <InputText
                        {...field}
                        placeholder="Enter FATCA ID"
                        className={`input-field appearance-none outline-none w-full `}
                        value={entityPlusData?.fatca_id || ""}
                        disabled={!isEditing}
                        onChange={(e) => {
                          field.onChange(e.target.value);
                          handleInputChange(
                            field.name,
                            e.target.value,
                            entityPlusData,
                            setEntityPlusData,
                          );
                        }}
                      />
                    );
                  }}
                />
              </div>
              <div className="field col-6 general-info-margin">
                <label className="input-label">
                  EORI (Economic Operators Registration and Identification)
                </label>
                <Controller
                  name="eori"
                  control={control}
                  render={({ field }) => {
                    return (
                      <InputText
                        {...field}
                        placeholder="Enter EORI"
                        className={`input-field appearance-none outline-none w-full `}
                        value={entityPlusData?.eori || ""}
                        disabled={!isEditing}
                        onChange={(e) => {
                          field.onChange(e.target.value);
                          handleInputChange(
                            field.name,
                            e.target.value,
                            entityPlusData,
                            setEntityPlusData,
                          );
                        }}
                      />
                    );
                  }}
                />
              </div>
              <div className="field col-6 general-info-margin">
                <label className="input-label">Local Tax ID</label>
                <Controller
                  name="local_tax_id"
                  control={control}
                  render={({ field }) => {
                    return (
                      <InputText
                        {...field}
                        placeholder="Enter Local Tax ID"
                        className={`input-field appearance-none outline-none w-full `}
                        value={entityPlusData?.local_tax_id || ""}
                        disabled={!isEditing}
                        onChange={(e) => {
                          field.onChange(e.target.value);
                          handleInputChange(
                            field.name,
                            e.target.value,
                            entityPlusData,
                            setEntityPlusData,
                          );
                        }}
                      />
                    );
                  }}
                />
              </div>
            </div>
          </div>

          {/* Global Entity identifiers */}
          <div className="flex flex-column gap-4 mb-4 w-full">
            <div className="flex flex-column gap-1">
              <p className="section-header">Global Entity Identifiers</p>
              <p className="section-description">
                Used to identify and verify legal entities across different
                jurisdictions
              </p>
            </div>

            <div className=" formgrid grid entity-plus-section">
              <div className="field col-6 general-info-margin">
                <label className="input-label">
                  DUNS (Data Universal Numbering System)
                </label>
                <Controller
                  name="duns"
                  control={control}
                  render={({ field }) => {
                    return (
                      <InputText
                        {...field}
                        placeholder="Enter DUNS"
                        className={`input-field appearance-none outline-none w-full `}
                        value={entityPlusData?.duns || ""}
                        disabled={!isEditing}
                        onChange={(e) => {
                          field.onChange(e.target.value);
                          handleInputChange(
                            field.name,
                            e.target.value,
                            entityPlusData,
                            setEntityPlusData,
                          );
                        }}
                      />
                    );
                  }}
                />
              </div>
              <div className="field col-6 general-info-margin">
                <label className="input-label">
                  LEI Code (Legal Entity Identifier)
                </label>
                <Controller
                  name="lei_code"
                  control={control}
                  render={({ field }) => {
                    return (
                      <InputText
                        {...field}
                        placeholder="Enter LEI Code"
                        className={`input-field appearance-none outline-none w-full `}
                        value={entityPlusData?.lei_code || ""}
                        disabled={!isEditing}
                        onChange={(e) => {
                          field.onChange(e.target.value);
                          handleInputChange(
                            field.name,
                            e.target.value,
                            entityPlusData,
                            setEntityPlusData,
                          );
                        }}
                      />
                    );
                  }}
                />
              </div>
              <div className="field col-6 general-info-margin">
                <label className="input-label">Local Employer Number</label>
                <Controller
                  name="local_employer_number"
                  control={control}
                  render={({ field }) => {
                    return (
                      <InputText
                        {...field}
                        placeholder="Enter local employer number"
                        className={`input-field appearance-none outline-none w-full `}
                        value={entityPlusData?.local_employer_number || ""}
                        disabled={!isEditing}
                        onChange={(e) => {
                          field.onChange(e.target.value);
                          handleInputChange(
                            field.name,
                            e.target.value,
                            entityPlusData,
                            setEntityPlusData,
                          );
                        }}
                      />
                    );
                  }}
                />
              </div>
            </div>
          </div>

          {/* Classification codes */}
          <div className="flex flex-column gap-4 mb-4 w-full">
            <div className="flex flex-column gap-1">
              <p className="section-header">Classification Codes</p>
              <p className="section-description">
                These fields describe the industry or economic activity the
                entity is involved in
              </p>
            </div>

            <div className=" formgrid grid entity-plus-section">
              <div className="field col-6 general-info-margin">
                <label className="input-label">
                  NACE (Nomenclature of Economic Activities – EU)
                </label>
                <Controller
                  name="nace"
                  control={control}
                  render={({ field }) => {
                    return (
                      <InputText
                        {...field}
                        placeholder="Enter NACE code"
                        className={`input-field appearance-none outline-none w-full `}
                        value={entityPlusData?.nace || ""}
                        disabled={!isEditing}
                        onChange={(e) => {
                          field.onChange(e.target.value);
                          handleInputChange(
                            field.name,
                            e.target.value,
                            entityPlusData,
                            setEntityPlusData,
                          );
                        }}
                      />
                    );
                  }}
                />
              </div>
              <div className="field col-6 general-info-margin">
                <label className="input-label">SIC/NAICS</label>
                <Controller
                  name="sic_naics"
                  control={control}
                  render={({ field }) => {
                    return (
                      <InputText
                        {...field}
                        placeholder="Enter SIC/NAICS"
                        className={`input-field appearance-none outline-none w-full `}
                        value={entityPlusData?.sic_naics || ""}
                        disabled={!isEditing}
                        onChange={(e) => {
                          field.onChange(e.target.value);
                          handleInputChange(
                            field.name,
                            e.target.value,
                            entityPlusData,
                            setEntityPlusData,
                          );
                        }}
                      />
                    );
                  }}
                />
              </div>
            </div>
          </div>

          {/* Financial information */}
          <div className="flex flex-column gap-4 mb-4 w-full">
            <div className="flex flex-column gap-1">
              <p className="section-header">Financial Information</p>
              <p className="section-description">
                Details about financial operations and reporting preferences:
              </p>
            </div>

            <div className=" formgrid grid entity-plus-section">
              <div className="field col-6 general-info-margin">
                <label className="input-label">Functional Currency</label>
                <Controller
                  name="currency"
                  control={control}
                  render={({ field }) => {
                    return (
                      <Dropdown
                        {...field}
                        placeholder="Select currency"
                        className={`w-full dropdown-field `}
                        options={currencyList}
                        itemTemplate={currencyItem}
                        valueTemplate={valueTemplate}
                        filter
                        filterBy="label,value"
                        value={entityPlusData?.currency || ""}
                        disabled={!isEditing}
                        onChange={(e) => {
                          field.onChange(e.target.value);
                          handleInputChange(
                            field.name,
                            e.target.value,
                            entityPlusData,
                            setEntityPlusData,
                          );
                        }}
                      />
                    );
                  }}
                />
              </div>
            </div>
          </div>

          {/* Entity lifecycle */}
          <div className="flex flex-column gap-4 w-full">
            <div className="flex flex-column gap-1">
              <p className="section-header">Entity Lifecycle</p>
              <p className="section-description">
                Tracks key milestones and operational status
              </p>
            </div>

            <div className=" formgrid grid entity-plus-section">
              <div className="w-full mb-1">
                <div className="field col-6 mb-0">
                  <label className="input-label">Incorporation Date</label>
                  <Controller
                    name="incorporation_date"
                    control={control}
                    render={({ field }) => {
                      return (
                        <>
                          <Calendar
                            {...field}
                            placeholder="MM/DD/YY"
                            className={`w-full calendar-field`}
                            value={removeTimezoneFromDateString(
                              entityPlusData?.incorporation_date,
                            )}
                            disabled={!isEditing}
                            onChange={(e) => {
                              field.onChange(e.target.value);
                              handleInputChange(
                                field.name,
                                formatDateToISOStartOfDay(e.target.value),
                                entityPlusData,
                                setEntityPlusData,
                              );
                            }}
                          />
                          <span className="pi pi-calendar incorporationDateIcon"></span>
                        </>
                      );
                    }}
                  />
                </div>
              </div>
              <div className="field col-6 general-info-margin">
                <label className="input-label">Acquisition Date</label>
                <Controller
                  name="acquisition_date"
                  control={control}
                  render={({ field }) => {
                    return (
                      <>
                        <Calendar
                          {...field}
                          placeholder="MM/DD/YY"
                          className={`w-full calendar-field `}
                          value={removeTimezoneFromDateString(
                            entityPlusData?.acquisition_date,
                          )}
                          disabled={!isEditing}
                          onChange={(e) => {
                            field.onChange(e.target.value);
                            handleInputChange(
                              field.name,
                              formatDateToISOStartOfDay(e.target.value),
                              entityPlusData,
                              setEntityPlusData,
                            );
                          }}
                        />
                        <span className="pi pi-calendar saleDetailsIcon"></span>
                      </>
                    );
                  }}
                />
              </div>
              <div className="field col-6 general-info-margin">
                <label className="input-label">Sale/Closure Details</label>
                <Controller
                  name="sale_closure_date"
                  control={control}
                  render={({ field }) => {
                    return (
                      <>
                        <Calendar
                          {...field}
                          placeholder="MM/DD/YY"
                          className={`w-full calendar-field`}
                          value={removeTimezoneFromDateString(
                            entityPlusData?.sale_closure_date,
                          )}
                          disabled={!isEditing}
                          onChange={(e) => {
                            field.onChange(e.target.value);
                            handleInputChange(
                              field.name,
                              formatDateToISOStartOfDay(e.target.value),
                              entityPlusData,
                              setEntityPlusData,
                            );
                          }}
                        />
                        <span className="pi pi-calendar saleDetailsIcon"></span>
                      </>
                    );
                  }}
                />
              </div>
              <div className="field col-12 general-info-margin">
                <label className="input-label">Sales / Closure Remarks</label>
                <Controller
                  name="sale_closure_remarks"
                  control={control}
                  render={({ field }) => {
                    return (
                      <InputTextarea
                        {...field}
                        placeholder="Enter remarks"
                        className={`input-field appearance-none outline-none w-full `}
                        rows={3}
                        value={entityPlusData?.sale_closure_remarks || ""}
                        disabled={!isEditing}
                        onChange={(e) => {
                          field.onChange(e.target.value);
                          handleInputChange(
                            field.name,
                            e.target.value,
                            entityPlusData,
                            setEntityPlusData,
                          );
                        }}
                      />
                    );
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};
EntityPlusForm.propTypes = {
  setBtnToggle: PropTypes.func,
  setBtnToggleAdd: PropTypes.func,
  entityPlusData: PropTypes.object,
  setEntityPlusData: PropTypes.func,
  isEditing: PropTypes.bool,
  editMode: PropTypes.bool,
  handleFormStateChange: PropTypes.func,
};
export default EntityPlusForm;
